import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import {
  ApiKeyFormPageRequestedAction,
  ApiKeyErroredAction,
  ApiKeyLoadedAction,
  CreateApiKeyAction,
  UpdateApiKeyAction,
  ApiKeyCreatedAction,
  ApiKeyUpdatedAction,
  apiKeyFormProjectsActions,
} from './api-key-form.actions';
import { ApiKeyService } from '../api-key/api-key.service';
import { ApiKeyFormCrudEffects } from './api-key-form-crud.effects';
import { FormMode } from './api-key-form.model';
import { ApiKeyFormApiError, ApiKeyFormApiErrorTypes } from './api-key-form.errors';
import { getErrorType } from 'src/app/shared/utils/error.utils';
import { of } from 'rxjs';
import { ProjectService } from '../project/project.service';
import { ProjectSortOrderType } from '../project/project.reducer';

@Injectable()
export class ApiKeyFormEffects extends ApiKeyFormCrudEffects {
  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(apiKeyFormProjectsActions.filterChanged),
      switchMap(({ filter }) =>
        this.projectService.getProjectSummaries(ProjectSortOrderType.DateDesc, filter).pipe(
          map((entities) => apiKeyFormProjectsActions.loadSucceeded({ entities })),
          catchError((error) => of(apiKeyFormProjectsActions.loadErrored({ error }))),
        ),
      ),
    ),
  );

  getKeyById$ = this.getApiKeyByIdEffect(
    ApiKeyFormPageRequestedAction,
    ApiKeyLoadedAction,
    ApiKeyErroredAction,
  );

  createApiKey$ = this.createApiKeyEffect(
    CreateApiKeyAction,
    ApiKeyCreatedAction,
    ApiKeyErroredAction,
  );

  redirectAfterCreated$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ApiKeyCreatedAction),
        tap(({ apiKey }) =>
          this.router.navigate(['/api-keys', apiKey.id], {
            state: { previousFormMode: FormMode.Create, generatedApiKey: apiKey.apiKey },
          }),
        ),
      ),
    {
      dispatch: false,
    },
  );

  updateApiKey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateApiKeyAction),
      concatMap(({ apiKeyId, apiKey: key }) =>
        this.apiKeyService.updateApiKey(apiKeyId, key).pipe(
          map((apiKey) => ApiKeyUpdatedAction({ apiKey })),
          catchError((err) =>
            of(
              ApiKeyErroredAction({
                error: new ApiKeyFormApiError(
                  getErrorType(ApiKeyFormApiErrorTypes, err.status),
                  err,
                ),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  redirectAfterUpdated$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ApiKeyUpdatedAction),
        tap(({ apiKey }) =>
          this.router.navigate(['/api-keys', apiKey.id], {
            state: { previousFormMode: FormMode.Edit, generatedApiKey: apiKey.apiKey },
          }),
        ),
      ),
    {
      dispatch: false,
    },
  );

  constructor(
    protected actions$: Actions,
    protected apiKeyService: ApiKeyService,
    private router: Router,
    private projectService: ProjectService,
  ) {
    super(actions$, apiKeyService);
  }
}
